import React from 'react';
import ReactDOM from 'react-dom';
import { LiveChatBootstrap } from '@shopee/channel-live-chat';
import App from '@/App';
import coreStore from '@/stores/core';
import { EventType, DIALOGUE_TYPE } from '@/types/common';
import chatbotCommonVariable from './common';
import { handlePostEndSessionRequest } from '@/utils/tools';

const platform = process.env.platform;

// [live-chat-split-change]
// 安全获取Live Chat SDK的方法
let liveChatSDKresolve: any = () => {};
export const safeCallLiveChatSDK = async (cb: (sdk: typeof window.LiveChatSDK) => any) => {
  if (!window.LiveChatSDK) {
    await new Promise((resolve) => {
      liveChatSDKresolve = resolve;
    });
  }
  return cb(window.LiveChatSDK);
};

// 初始化Live-Chat SDK
export default async function initLiveChatSDK() {
  const LiveChatSDK = await LiveChatBootstrap.init({ platform: 'app', region: COUNTRY, env: ENVIRONMENT, tenant: platform });
  liveChatSDKresolve(LiveChatSDK);

  // Live Chat SDK相关事件监听
  LiveChatSDK.on(EventType.BeforeInit, async () => {
    // console.log('【Live-Chat-SDK】:', 'BeforeInit');
    // 进线前把input聚焦状态置为false，防止page级别remove scroll开启
    const { default: firstInit } = await import('@/stores/firstInit');
    const { default: inputStore } = await import('@/stores/input');
    inputStore.UPDATE_INPUT_FOCUS(false);
    firstInit.UPDATE_INPUT_FOCUS(false);
  });

  LiveChatSDK.on(EventType.Init, async () => {
    // console.log('【Live-Chat-SDK】:', 'Init');
  });

  LiveChatSDK.on(EventType.Queueing, async () => {
    // console.log('【Live-Chat-SDK】:', 'Queueing');
    const { default: agentStore } = await import('@/stores/agent');
    agentStore.UPDATE_IN_AGENT(true);
    agentStore.FINISH_LOADING_IN_AGENT();
  });

  LiveChatSDK.on(EventType.CancelQueue, async (data: any) => {
    // console.log('【Live-Chat-SDK】:', 'CancelQueue', options);
    const { default: initStore } = await import('@/stores/init');
    initStore.UPDATE_cancelFromAgent();
    const { showContactMessage = false, hideWebformLink = false } = data || {};
    if (showContactMessage) {
      const { default: initStore } = await import('@/stores/init');
      initStore.UPDATE_cancelFromAgent();
      const { pushContactUsAgainMessage } = await import('@/utils/agent');
      pushContactUsAgainMessage(true, hideWebformLink);
    }
  });

  LiveChatSDK.on(EventType.Connect, async (options: any) => {
    // console.log('【Live-Chat-SDK】:', 'Connect', options);
    const { sessionId, initMessageId } = options;
    if (!sessionId) {
      return;
    }
    /** 2023.12.11 更新VIRTUAL_CALL_NEED_RECOVER_HISTORY为false，历史原因，由于从floating bubble回来没有带from，所以在租户判断时，默认最开始会将
    租户判断为默认的shopee，知道setSearchUrl执行完成，其中收起影响的主要是getDataFromLocalStorage中的接口请求的域名，为了减少其影响，默认从localstorage里面去数据，如果有，则不再请求接口，所以需要先在localstorage中塞数据
    **/
    chatbotCommonVariable?.UPDATE_VIRTUAL_CALL_NEED_RECOVER_HISTORY(false);
    const { default: agentStore } = await import('@/stores/agent');
    agentStore.FINISH_LOADING_IN_AGENT();
    const { default: sessionIDListStore } = await import('@/stores/sessionIDList');
    const { saveRecoveryContext } = await import('@/utils/tools');
    saveRecoveryContext();
    sessionIDListStore.UPDATE_SESSION_ID_LIST({ type: 'agent', id: sessionId });
    const { isSupportChatbot } = await import('@/config/core');
    // 如果有chatbot的话，在chatbot的init session的时候会更新historyStore的initMessageId和BeforeMessageId
    // 如果没有chatbot的话，不存在不退出页面的情况下再次进线的情况，所以进线需要更新history store上一次的消息id
    if (initMessageId && !isSupportChatbot()) {
      const { default: historyStore } = await import('@/stores/history');
      historyStore.setInitMessageId(initMessageId);
      historyStore.setBeforeMessageId(initMessageId);
    }
  });

  LiveChatSDK.on(EventType.Connected, async () => {
    // console.log('【Live-Chat-SDK】:', 'Connected');
    const { default: agentStore } = await import('@/stores/agent');
    agentStore.UPDATE_IN_AGENT(true);
    agentStore.FINISH_LOADING_IN_AGENT();
  });

  LiveChatSDK.on(EventType.Chatting, async () => {
    // console.log('【Live-Chat-SDK】:', 'Chatting', messageItem);
  });

  LiveChatSDK.on(EventType.EndChat, async () => {
    // console.log('【Live-Chat-SDK】:', 'EndChat', options);
    const { default: agentStore } = await import('@/stores/agent');
    agentStore.UPDATE_IN_AGENT(false);
    agentStore.FINISH_LOADING_IN_AGENT();
  });

  LiveChatSDK.on(EventType.Quit, async (data: any) => {
    const { showContactMessage = false, hideWebformLink = false } = data || {};
    const { default: agentStore } = await import('@/stores/agent');
    agentStore.UPDATE_IN_AGENT(false);
    agentStore.FINISH_LOADING_IN_AGENT();
    agentStore.UPDATE_HAS_END_LIVECHAT(true);  // 标记live chat session已结束
    ReactDOM.render(<App stores={{ coreStore }} />, document.getElementById('app'));
    const initStore = coreStore.safeGetStore('initStore');
    initStore.UPDATE_cancelFromAgent();
    initStore.UPDATE_NOTFIRST();
    if (showContactMessage) {
      const { pushContactUsAgainMessage } = await import('@/utils/agent');
      pushContactUsAgainMessage(true, hideWebformLink);
    }
  });

  LiveChatSDK.on(EventType.ChatWithAgent, async () => {
    const { handleLiveBtnClick } = await import('@/utils/btnClickHandler');
    const { getTrans } = await import('@/utils/tools');
    handleLiveBtnClick({
      dialogueId: '',
      dialogueType: DIALOGUE_TYPE.Live,
      text: getTrans('btn_live'),
      isHistory: false,
    });
  });

  // 页面相关事件
  LiveChatSDK.on(EventType.PopWebView, async () => {
    // console.log('【Live-Chat-SDK】:', 'PopWebView');
    const { saveRecoveryContext, handlePostEndSessionRequest } = await import('@/utils/tools');
    // 存储需要恢复的live chat上下文，因为用户可能未登陆，所以不更新currentUser信息
    saveRecoveryContext({ shouldUpdateCurrentUser: false });

    handlePostEndSessionRequest();
  });
}

